import { BellIcon } from '@eleveo/icons-react';
import { Button, Col, Container, Pagination, Row, Table, usePagination } from '@zoom/components';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { EllipsisContent } from '../../components/EllipsisContent';
import { FullSizeMessage } from '../../components/FullSizeMessage';
import Icon from '../../components/Icon';
import { DeleteIcon } from '../../components/ListIcons/DeleteIcon';
import { EditIcon } from '../../components/ListIcons/EditIcon';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { ModalConfirmation } from '../../components/ModalConfirmation';
import { Content } from '../../components/layout/Content';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  AdherenceSetting,
  GetAdherenceNotificationSetupDocument,
  useCreateAdherenceNotificationMutation,
  useCreateAdherenceSettingMutation,
  useDeleteAdherenceNotificationMutation,
  useDeleteAdherenceSettingMutation,
  useGetAdherenceNotificationSetupQuery,
  useGetAdherenceSettingsDataQuery,
  useGetAdherenceSettingsQuery,
  useUpdateAdherenceNotificationMutation,
  useUpdateAdherenceSettingMutation,
} from '../../graphql/components';
import { convertToMinutesSeconds } from '../../utils/dateRange';
import { nameOf } from '../../utils/helpers';
import { getDuplicatedReasonCodes } from './ACDStatusLabel';
import { AdherenceNotificationFormSidebar, AdherenceNotificationFormValues } from './AdherenceNotificationFormSidebar';
import { AdherenceSettingACDStatuses } from './AdherenceSettingACDStatuses';
import { AdherenceSettingScheduleActivities } from './AdherenceSettingScheduleActivities';
import { AdherenceSettingFormValues, AdherenceSettingsFormSidebar, parseValues } from './AdherenceSettingsFormSidebar';
import {
  ADD_SETTING_BUTTON_TAG,
  CONTAINER_TAG,
  getListHeaderTag,
  getListItemTag,
  LIST_CONTAINER_TAG,
  LIST_PAGINATION_TAG,
  NOTIFICATION_SETTINGS_BUTTON_TAG,
  TITLE_TAG,
} from './constants';

const Header: React.FC<{
  onCreateButtonClick: () => void;
  onNotificationButtonClick: () => void;
  isCreateButtonActive: boolean;
  isNotificationButtonActive: boolean;
}> = ({ onCreateButtonClick, onNotificationButtonClick, isCreateButtonActive, isNotificationButtonActive }) => {
  const { userHasPermissions } = useAuth();
  const { t } = useTranslation();

  return (
    <Row>
      {userHasPermissions([Permissions.CreateAdherenceSettings]) && (
        <Col sm="auto">
          <Button
            active={isCreateButtonActive}
            className="button--icon--left-hand-side"
            color="info"
            data-test-id={ADD_SETTING_BUTTON_TAG}
            onClick={onCreateButtonClick}
          >
            <Icon color="#239ed8" kind="plus" size={20} />
            {t('scheduleAdherence.addStatus')}
          </Button>
        </Col>
      )}

      {userHasPermissions([Permissions.ViewRtaNotificationSettings, Permissions.EditRtaNotificationSettings]) && (
        <Col sm="auto" className="ml-auto">
          <Button
            active={isNotificationButtonActive}
            className="button--icon--left-hand-side"
            color="primary"
            data-test-id={NOTIFICATION_SETTINGS_BUTTON_TAG}
            onClick={onNotificationButtonClick}
          >
            <BellIcon className="text-xl" />
            {t('scheduleAdherence.realTimeAdherence.rtaNotifications')}
          </Button>
        </Col>
      )}
    </Row>
  );
};

const defaultQueryData = {
  totalCount: 0,
};

export const AdherenceSettingsScene = () => {
  const { t } = useTranslation();
  const { userHasPermissions } = useAuth();
  const { closeSidebar, isSidebarOpen, openSidebar } = useSidebar();

  const [queryOptions, setQueryOptions] = useState(defaultQueryData);
  const [settingToEdit, setSettingToEdit] = useState<AdherenceSetting | null>(null);
  const [settingToDelete, setSettingToDelete] = useState<AdherenceSetting | null>(null);

  const [isCreateSidebarOpen, setCreateSidebarOpen] = useState(false);
  const [isNotificationSidebarOpen, setNotificationSidebarOpen] = useState(false);

  const { activePage, itemsPerPage: max, offset: first, setPage, totalPages } = usePagination(queryOptions.totalCount);

  const {
    data: settings,
    loading: settingsLoading,
    error: settingsError,
    refetch: refetchSettings,
  } = useGetAdherenceSettingsQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    variables: {
      searchCriteria: {
        first,
        max,
      },
    },
    onCompleted: ({ adherenceSettings }) => {
      setQueryOptions((options) => ({
        ...options,
        totalCount: adherenceSettings.totalCount,
      }));
    },
  });
  const { data, loading: dataLoading, error: dataError } = useGetAdherenceSettingsDataQuery();
  const [createSetting, { loading: createSettingLoading }] = useCreateAdherenceSettingMutation();
  const [updateSetting, { loading: updateSettingLoading }] = useUpdateAdherenceSettingMutation();
  const [deleteSetting, { loading: deleteSettingLoading }] = useDeleteAdherenceSettingMutation();

  const hasViewRtaSettingsPermission = userHasPermissions([Permissions.ViewRtaNotificationSettings]);

  // Adherence notification setup
  const {
    loading: notificationSetupLoading,
    error: notificationSetupError,
    data: { adherenceNotificationSetup } = {},
  } = useGetAdherenceNotificationSetupQuery({
    skip: !hasViewRtaSettingsPermission,
    // always fetch but store in cache
    fetchPolicy: 'network-only',
  });
  const notificationSetup =
    typeof adherenceNotificationSetup?.threshold === 'number' ? adherenceNotificationSetup : null;

  const [createNotification, { loading: createNotificationLoading }] = useCreateAdherenceNotificationMutation();
  const [updateNotification, { loading: updateNotificationLoading }] = useUpdateAdherenceNotificationMutation();
  const [deleteNotification, { loading: deleteNotificationLoading }] = useDeleteAdherenceNotificationMutation();

  const loading = settingsLoading || dataLoading || notificationSetupLoading;
  const error = settingsError || dataError || notificationSetupError;
  const mutationInProgress =
    createSettingLoading ||
    updateSettingLoading ||
    deleteSettingLoading ||
    createNotificationLoading ||
    updateNotificationLoading ||
    deleteNotificationLoading;

  const hasDeletePermission = userHasPermissions([Permissions.DeleteAdherenceSettings]);
  const hasEditPermission = userHasPermissions([Permissions.EditAdherenceSettings]);
  const hasModifyPermissions = hasEditPermission || hasDeletePermission;

  const refetchSettingsFirstPage = async () => {
    if (activePage === 1) {
      await refetchSettings({
        searchCriteria: {
          first: 0,
          max,
        },
      });
    } else {
      setPage(1);
    }
  };

  const handlePrepareCreate = () => {
    setNotificationSidebarOpen(false);
    setSettingToEdit(null);

    if (isSidebarOpen && isCreateSidebarOpen) {
      closeSidebar();
      setCreateSidebarOpen(false);
    } else {
      openSidebar();
      setCreateSidebarOpen(true);
    }
  };

  const handlePrepareNotificationSetup = () => {
    setCreateSidebarOpen(false);
    setSettingToEdit(null);

    if (isSidebarOpen && isNotificationSidebarOpen) {
      closeSidebar();
      setNotificationSidebarOpen(false);
    } else {
      openSidebar();
      setNotificationSidebarOpen(true);
    }
  };

  const handleCreate = async (values: AdherenceSettingFormValues) => {
    try {
      await createSetting({
        variables: {
          setting: parseValues(values),
        },
      });

      await refetchSettingsFirstPage();

      closeSidebar();
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleCreateNotification = async (values: AdherenceNotificationFormValues) => {
    try {
      await createNotification({
        variables: {
          notificationSetup: {
            threshold: convertToMinutesSeconds(values.threshold),
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [GetAdherenceNotificationSetupDocument],
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      closeSidebar();
      setNotificationSidebarOpen(false);
    }
  };

  const handlePrepareEdit = (setting: AdherenceSetting) => {
    setNotificationSidebarOpen(false);
    setCreateSidebarOpen(false);

    if (!isSidebarOpen) {
      openSidebar();
    }

    setSettingToEdit(setting);
  };

  const handleUpdate = async (values: AdherenceSettingFormValues) => {
    if (settingToEdit === null) {
      return;
    }

    try {
      await updateSetting({
        variables: {
          id: settingToEdit.id,
          setting: parseValues(values),
        },
      });

      await refetchSettingsFirstPage();

      closeSidebar();
      setSettingToEdit(null);
    } catch (e) {
      // `customFetch()` will display error message
    }
  };

  const handleNotificationUpdate = async (values: AdherenceNotificationFormValues) => {
    try {
      await updateNotification({
        variables: {
          notificationSetup: {
            threshold: convertToMinutesSeconds(values.threshold),
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [GetAdherenceNotificationSetupDocument],
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      closeSidebar();
      setNotificationSidebarOpen(false);
    }
  };

  const handleShowDeleteModal = (setting: AdherenceSetting) => setSettingToDelete(setting);
  const handleCloseDeleteModal = () => setSettingToDelete(null);

  const handleDelete = async () => {
    if (!settingToDelete) {
      return;
    }

    const { id } = settingToDelete;

    try {
      await deleteSetting({
        variables: {
          id,
        },
      });

      await refetchSettingsFirstPage();
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setSettingToDelete(null);
    }
  };

  const handleNotificationDelete = async (values: AdherenceNotificationFormValues) => {
    if (values.pushNotifications) {
      return;
    }

    try {
      await deleteNotification({
        awaitRefetchQueries: true,
        refetchQueries: [GetAdherenceNotificationSetupDocument],
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      closeSidebar();
      setNotificationSidebarOpen(false);
    }
  };

  const handleSubmit = (values: AdherenceSettingFormValues) => {
    if (settingToEdit === null) {
      handleCreate(values);
    } else {
      handleUpdate(values);
    }
  };

  const handleNotificationSetupSubmit = (values: AdherenceNotificationFormValues) => {
    if (!notificationSetup && values.pushNotifications) {
      handleCreateNotification(values);
      return;
    }

    if (notificationSetup && !values.pushNotifications) {
      handleNotificationDelete(values);
      return;
    }

    if (notificationSetup && values.pushNotifications) {
      handleNotificationUpdate(values);
    }
  };

  const handleCancel = () => {
    closeSidebar();

    if (settingToEdit) {
      setSettingToEdit(null);
    }

    if (isNotificationSidebarOpen) {
      setNotificationSidebarOpen(false);
    }

    if (isCreateSidebarOpen) {
      setCreateSidebarOpen(false);
    }
  };

  return (
    <Content
      header={{
        actionBar: (
          <Header
            isCreateButtonActive={isCreateSidebarOpen}
            isNotificationButtonActive={isNotificationSidebarOpen}
            onCreateButtonClick={handlePrepareCreate}
            onNotificationButtonClick={handlePrepareNotificationSetup}
          />
        ),
        testId: TITLE_TAG,
        title: t('scheduleAdherence.adherenceSettings'),
        titleSlug: 'adherence-settings',
      }}
      sidebarContent={
        isNotificationSidebarOpen ? (
          <AdherenceNotificationFormSidebar
            notificationSetup={notificationSetup}
            onCancel={handleCancel}
            onSubmit={handleNotificationSetupSubmit}
          />
        ) : (
          <AdherenceSettingsFormSidebar
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            setting={settingToEdit}
            scheduleElements={data?.scheduleElements ?? []}
            eventTypes={data?.adherenceEventTypes ?? []}
            reasonCodes={data?.adherenceReasonCodes ?? []}
          />
        )
      }
    >
      <Container data-test-id={CONTAINER_TAG}>
        <LoadingOverlay loading={loading || mutationInProgress}>
          {(() => {
            if ((loading && !settings) || error) {
              return null;
            }

            if (
              !settings ||
              settings.adherenceSettings.adherenceSettings.length === 0 ||
              !data?.adherenceEventTypes ||
              !data?.adherenceReasonCodes
            ) {
              return <FullSizeMessage headline={t('scheduleAdherence.noEntries')} iconKind="no_data" />;
            }

            const { scheduleElements, adherenceEventTypes, adherenceReasonCodes } = data;
            const duplicatedReasonCodes = getDuplicatedReasonCodes(adherenceReasonCodes);

            return (
              <>
                <Table data-test-id={LIST_CONTAINER_TAG} striped fixedLayout withContainerPadding>
                  <thead>
                    <tr>
                      <th data-test-id={getListHeaderTag(nameOf<AdherenceSetting>('name'))}>
                        {t('scheduleAdherence.eleveoStatus')}
                      </th>
                      <th data-test-id={getListHeaderTag(nameOf<AdherenceSetting>('acdStatuses'))}>
                        {t('scheduleAdherence.acdStatus')}
                      </th>
                      <th data-test-id={getListHeaderTag(nameOf<AdherenceSetting>('historyScheduleActivityIds'))}>
                        {t('scheduleAdherence.adherenceHistory', { count: 1 })}
                      </th>
                      <th data-test-id={getListHeaderTag(nameOf<AdherenceSetting>('realTimeScheduleActivityIds'))}>
                        {t('scheduleAdherence.realTimeAdherence.rta')}
                      </th>
                      {hasModifyPermissions && (
                        <th data-test-id={getListHeaderTag('actions')}>{t('layout.action.actions')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {settings.adherenceSettings.adherenceSettings.map((setting, i) => (
                      <tr key={setting.id} className={settingToEdit?.id === setting.id ? 'table-active' : ''}>
                        <EllipsisContent
                          as="td"
                          data-test-id={getListItemTag(i, nameOf<AdherenceSetting>('name'))}
                          className="font-weight-bold"
                        >
                          {setting.name}
                        </EllipsisContent>
                        <td data-test-id={getListItemTag(i, nameOf<AdherenceSetting>('acdStatuses'))}>
                          <AdherenceSettingACDStatuses
                            statuses={setting.acdStatuses}
                            eventTypes={adherenceEventTypes}
                            reasonCodes={adherenceReasonCodes}
                            duplicatedReasonCodes={duplicatedReasonCodes}
                          />
                        </td>
                        <td
                          data-test-id={getListItemTag(i, nameOf<AdherenceSetting>('historyScheduleActivityIds'))}
                          className="text-wrap"
                        >
                          <AdherenceSettingScheduleActivities
                            scheduleActivityIds={setting.historyScheduleActivityIds}
                            scheduleElements={scheduleElements}
                          />
                        </td>
                        <td
                          data-test-id={getListItemTag(i, nameOf<AdherenceSetting>('realTimeScheduleActivityIds'))}
                          className="text-wrap"
                        >
                          <AdherenceSettingScheduleActivities
                            scheduleActivityIds={setting.realTimeScheduleActivityIds}
                            scheduleElements={scheduleElements}
                          />
                        </td>
                        {hasModifyPermissions && (
                          <td className="list-actions">
                            {hasEditPermission && (
                              <EditIcon testId={getListItemTag(i, 'edit')} onClick={() => handlePrepareEdit(setting)} />
                            )}
                            {hasDeletePermission && (
                              <DeleteIcon
                                enabled
                                testId={getListItemTag(i, 'delete')}
                                onClick={() => handleShowDeleteModal(setting)}
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Pagination
                  dataTestId={LIST_PAGINATION_TAG}
                  activePage={activePage}
                  totalPages={totalPages}
                  setPage={setPage}
                  buttons={{
                    first: t('layout.pagination.first'),
                    previous: t('layout.pagination.previous'),
                    next: t('layout.pagination.next'),
                    last: t('layout.pagination.last'),
                  }}
                />

                <ModalConfirmation
                  headerText={t('scheduleAdherence.deleteSetting')}
                  bodyText={
                    <Trans
                      i18nKey="scheduleAdherence.deleteSettingBody"
                      values={{ name: settingToDelete?.name }}
                      components={{ italic: <i /> }}
                    />
                  }
                  submitText={t('layout.action.delete')}
                  closeText={t('layout.action.cancel')}
                  onSubmit={handleDelete}
                  onClose={handleCloseDeleteModal}
                  show={settingToDelete !== null}
                />
              </>
            );
          })()}
        </LoadingOverlay>
      </Container>
    </Content>
  );
};
